import React from 'react';
import type { GameFieldConfig } from '../types/package';

interface DynamicGameSettingsFormProps {
  fieldConfigs: GameFieldConfig[];
  values: { [key: string]: any };
  onChange: (name: string, value: any) => void;
  errors?: { [key: string]: string };
}

export const DynamicGameSettingsForm: React.FC<DynamicGameSettingsFormProps> = ({
  fieldConfigs,
  values,
  onChange,
  errors = {}
}) => {
  const renderField = (config: GameFieldConfig) => {
    const value = values[config.name] ?? config.defaultValue ?? '';
    const error = errors[config.name];

    switch (config.type) {
      case 'string':
        return (
          <input
            type="text"
            id={config.name}
            value={value}
            onChange={(e) => onChange(config.name, e.target.value)}
            minLength={config.minLength}
            maxLength={config.maxLength}
            className={`input ${error ? 'border-red-500/50' : ''}`}
            required={config.required}
            placeholder={config.description}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            id={config.name}
            value={value}
            onChange={(e) => onChange(config.name, Number(e.target.value))}
            className={`input ${error ? 'border-red-500/50' : ''}`}
            required={config.required}
            min={config.min}
            max={config.max}
            placeholder={config.description}
          />
        );

      case 'boolean':
        return (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id={config.name}
              checked={Boolean(value)}
              onChange={(e) => onChange(config.name, e.target.checked)}
              className="checkbox"
            />
            <label htmlFor={config.name} className="text-sm text-gray-300">
              {config.description || config.label}
            </label>
          </div>
        );

      case 'select':
        return (
          <select
            id={config.name}
            value={value}
            onChange={(e) => onChange(config.name, e.target.value)}
            className={`input ${error ? 'border-red-500/50' : ''}`}
            required={config.required}
          >
            <option value="">Select</option>
            {config.options?.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );

      default:
        return null;
    }
  };

  if (fieldConfigs.length === 0) {
    return (
      <p className="text-sm text-gray-500 italic">
        No additional settings for this game.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="section-title">Game Settings</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {fieldConfigs.map((config) => (
          <div key={config.name}>
            {config.type !== 'boolean' && (
              <label htmlFor={config.name} className="text-sm text-gray-300 mb-1 block">
                {config.label}
                {config.required && <span className="text-red-400 ml-1">*</span>}
              </label>
            )}
            {renderField(config)}
            {config.description && config.type !== 'boolean' && (
              <p className="text-xs text-gray-500 mt-1">{config.description}</p>
            )}
            {errors[config.name] && (
              <p className="text-xs text-red-400 mt-1">{errors[config.name]}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
